export const BRANCH_SCHEDULES_SAVED_EVENT = "talento-humano:branch-schedules-saved";

export function notifyBranchSchedulesSaved(
  branchId: number,
  weekIndex?: number | null,
) {
  window.dispatchEvent(
    new CustomEvent(BRANCH_SCHEDULES_SAVED_EVENT, {
      detail: { branchId, weekIndex: weekIndex ?? undefined },
    }),
  );
}

export function normalizeScheduleSlotLabel(s: unknown): string {
  return String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

export function isScheduleItemCoveringEssentialSlot(
  scheduleItem: any,
  positionRow: {
    positionName?: string;
    shift?: string;
    positionOriginalId_?: number;
  },
): boolean {
  if (!scheduleItem?.show) return false;
  if (positionRow.shift != null && scheduleItem.shift !== positionRow.shift) {
    return false;
  }
  const slot = normalizeScheduleSlotLabel(positionRow.positionName);
  const assigned = normalizeScheduleSlotLabel(scheduleItem.position);
  if (slot.length > 0 && assigned.length > 0) {
    return assigned === slot;
  }
  return (
    Number(scheduleItem.positionOriginalId_) ===
    Number(positionRow.positionOriginalId_)
  );
}

export function normalizePositionIdKey(posId: unknown): string {
  if (posId == null || posId === "") return "";
  const n = Number(posId);
  if (!Number.isNaN(n)) return String(n);
  return String(posId).trim();
}

function shiftKey(shift: unknown): string {
  return String(shift ?? "")
    .trim()
    .toLowerCase();
}

export function dedupeWeeklyTableItems(items: any[]): any[] {
  const out: any[] = [];
  const seen = new Set<string>();
  for (const it of items) {
    const rowSid = Number(
      (it as any).branchScheduleId ?? (it as any).scheduleRowId ?? 0,
    );
    if (!Number.isNaN(rowSid) && rowSid > 0) {
      const k = `sid:${rowSid}`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(it);
      continue;
    }
    const sk = shiftKey(it.shift);
    const isDescanso =
      it.positionId == null &&
      (it.positionDisplayName === "Descanso" ||
        String(it.positionName || "").toLowerCase().includes("descanso"));
    if (isDescanso) {
      const k = `${it.partnerId}|${it.day}|${sk}|DESCANSO`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(it);
      continue;
    }
    const k = `${it.partnerId}|${it.day}|${sk}|${normalizePositionIdKey(it.positionId)}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(it);
  }
  return out;
}

export function dedupeBranchSchedulesForDisplay(
  list: any[],
  normalizeShift: (shift: any) => string,
  getPartnerId: (s: any) => number | undefined,
): any[] {
  const map = new Map<string, any>();
  for (const s of list) {
    const pid = getPartnerId(s);
    if (pid == null) continue;
    if (s.id != null && s.id !== undefined) {
      map.set(`id:${Number(s.id)}`, s);
      continue;
    }
    const posId = s.position?.id ?? s.position;
    const day = s.dayOfWeek;
    const shift = normalizeShift(s.shift);
    const key = `${pid}|${day}|${shift}|${normalizePositionIdKey(posId)}`;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, s);
    } else {
      const idNew = Number(s.id);
      const idOld = Number(existing.id);
      if (!Number.isNaN(idNew) && !Number.isNaN(idOld) && idNew < idOld) {
        map.set(key, s);
      }
    }
  }
  return Array.from(map.values());
}

export function normalizeWeeklyCellPartLabel(value: string): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");
}

/** Partes de celda semanal: "Puesto A, Puesto B" o "Puesto A (Vespertino)". */
export function splitWeeklyCellParts(cellValue: string): string[] {
  const raw = String(cellValue ?? "").trim();
  if (!raw || raw === "-" || raw === "DESCANSO") return [];
  return raw
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
}

export function weeklyCellPartMatchesScheduleName(
  cellPart: string,
  scheduleName: string,
): boolean {
  const part = normalizeWeeklyCellPartLabel(cellPart);
  const name = normalizeWeeklyCellPartLabel(scheduleName);
  if (!part || !name) return false;
  return (
    name === part ||
    name.startsWith(part + " ") ||
    part.startsWith(name + " ")
  );
}

export function appendWeeklyCellPart(
  cellValue: string,
  part: string,
): string {
  const next = String(part ?? "").trim();
  if (!next) return String(cellValue ?? "").trim();
  const existing = splitWeeklyCellParts(cellValue);
  if (existing.some((p) => p.toLowerCase() === next.toLowerCase())) {
    return existing.join(", ");
  }
  return existing.length ? `${existing.join(", ")}, ${next}` : next;
}

export function replaceWeeklyCellPartsForShift(
  cellValue: string,
  rowShift: string,
  newPartsForShift: string[],
  belongsToShift: (part: string, shift: string) => boolean,
): string {
  const kept = splitWeeklyCellParts(cellValue).filter(
    (part) => !belongsToShift(part, rowShift),
  );
  const added = newPartsForShift.map((p) => p.trim()).filter(Boolean);
  const merged = [...kept, ...added];
  return merged.join(", ");
}

/** Reemplaza una sola etiqueta de puesto en la celda (p. ej. al hacer clic en una tarjeta concreta). */
export function replaceOneWeeklyCellPart(
  cellValue: string,
  targetPartLabel: string,
  newPart: string,
): string {
  const target = String(targetPartLabel ?? "").trim();
  const parts = splitWeeklyCellParts(cellValue);
  if (!target) {
    const next = String(newPart ?? "").trim();
    return next;
  }
  let removed = false;
  const kept = parts.filter((part) => {
    if (!removed && weeklyCellPartMatchesScheduleName(part, target)) {
      removed = true;
      return false;
    }
    return true;
  });
  const next = String(newPart ?? "").trim();
  if (next) kept.push(next);
  return kept.join(", ");
}

export function countWeeklyCellPartsForShift(
  cellValue: string,
  rowShift: string,
  belongsToShift: (part: string, shift: string) => boolean,
): number {
  return splitWeeklyCellParts(cellValue).filter((part) =>
    belongsToShift(part, rowShift),
  ).length;
}

export function dedupePartnerDayShiftPositionItems(items: any[]): any[] {
  const seen = new Set<string>();
  const out: any[] = [];
  for (const it of items) {
    const rowSid = Number(
      (it as any).branchScheduleId ?? (it as any).scheduleRowId ?? 0,
    );
    if (!Number.isNaN(rowSid) && rowSid > 0) {
      const k = `sid:${rowSid}`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(it);
      continue;
    }
    const k = `${it.day}|${it.shift}|${it.partnerId}|${normalizePositionIdKey(it.positionId)}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(it);
  }
  return out;
}
